import React, { useState } from "react";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Divider } from "primereact/divider";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import MediaBrowser from "./MediaBrowser";
import { updateMedia, deleteMedia, formatBytes, isImage, isVideo } from "../../api/mediaApi";
import "./media.scss";

const VISIBILITY_OPTIONS = [
  { label: "Whole school — anyone on staff can reuse it", value: "school" },
  { label: "Public — also visible on your public website", value: "public" },
  { label: "Private — only me and school admins", value: "private" },
];

/**
 * The school's media library. Everything uploaded here is reusable: pick it as
 * the school logo, drop it into a website page, attach it to an announcement.
 * Assets never leave the school's own tenant schema.
 */
const MediaLibrary = () => {
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Transient inline confirmations, so routine actions don't fire a toast at
  // someone who is working through a dozen files.
  const [flash, setFlash] = useState(null);

  const showFlash = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash((f) => (f === msg ? null : f)), 2000);
  };

  const select = (item) => {
    setSelected(item);
    setDraft({
      title: item.title || "",
      alt_text: item.alt_text || "",
      folder: item.folder || "General",
      visibility: item.visibility || "school",
      tags: Array.isArray(item.tags) ? item.tags : [],
    });
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await updateMedia(selected.id, draft);
      setSelected({ ...selected, ...res.item });
      setRefreshKey((k) => k + 1);
      showFlash("Saved");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save details");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!selected) return;
    confirmDialog({
      message:
        "Delete this file permanently? Anything still pointing at it — a logo, a website image — will show a broken image.",
      header: "Delete media",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteMedia(selected.id);
          setSelected(null);
          setDraft(null);
          setRefreshKey((k) => k + 1);
          // The file vanishing from the grid is the confirmation.
        } catch (err) {
          toast.error(err.response?.data?.message || "Could not delete file");
        }
      },
    });
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${selected.file_path}`);
      // Nothing visibly changes on a copy, so this one does need saying —
      // inline rather than as a toast.
      showFlash("Link copied");
    } catch {
      toast.info(selected.file_path);
    }
  };

  return (
    <div className="p-4">
      <ConfirmDialog />
      <div className="mb-4">
        <h2 className="m-0 text-2xl font-bold">Media Library</h2>
        <p className="mt-1 text-color-secondary">
          One shared file store for your school — upload once, then reuse the same image
          for the school logo, website pages and anywhere else a file is needed.
        </p>
      </div>

      <div className="media-page">
        <Card>
          <MediaBrowser
            selectedId={selected?.id}
            onSelect={select}
            refreshKey={refreshKey}
          />
        </Card>

        <Card className="media-details" title={selected ? "File details" : "No file selected"}>
          {!selected ? (
            <p className="m-0 text-color-secondary">
              Select a file to edit its title, alt text, folder and visibility.
            </p>
          ) : (
            <>
              <div className="media-details-preview">
                {isImage(selected) ? (
                  <img src={selected.file_path} alt={selected.alt_text || ""} />
                ) : (
                  <i className={isVideo(selected) ? "pi pi-video" : "pi pi-file"} />
                )}
              </div>

              <div className="flex flex-column gap-3">
                <div>
                  <label htmlFor="mediaTitle" className="block mb-1">Title</label>
                  <InputText
                    id="mediaTitle"
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div>
                  <label htmlFor="mediaAlt" className="block mb-1">Alt text</label>
                  <InputText
                    id="mediaAlt"
                    value={draft.alt_text}
                    onChange={(e) => setDraft({ ...draft, alt_text: e.target.value })}
                    className="w-full"
                    placeholder="Describes the image for screen readers"
                  />
                </div>
                <div>
                  <label htmlFor="mediaFolder" className="block mb-1">Folder</label>
                  <InputText
                    id="mediaFolder"
                    value={draft.folder}
                    onChange={(e) => setDraft({ ...draft, folder: e.target.value })}
                    className="w-full"
                    placeholder="e.g. Branding, Events, Newsletters"
                  />
                </div>
                <div>
                  <label htmlFor="mediaVisibility" className="block mb-1">Visibility</label>
                  <Dropdown
                    id="mediaVisibility"
                    value={draft.visibility}
                    options={VISIBILITY_OPTIONS}
                    onChange={(e) => setDraft({ ...draft, visibility: e.value })}
                    className="w-full"
                  />
                  <small className="block mt-1 text-color-secondary">
                    {draft.visibility === "public"
                      ? "Anyone on the internet can open this file's link. Use it for logos and gallery images."
                      : "Only people signed in to your school can open this file's link."}
                  </small>
                </div>
                <div>
                  <label htmlFor="mediaTags" className="block mb-1">Tags</label>
                  <Chips
                    id="mediaTags"
                    value={draft.tags}
                    onChange={(e) => setDraft({ ...draft, tags: e.value || [] })}
                    className="w-full"
                    separator=","
                  />
                </div>
              </div>

              <Divider />

              <ul className="list-none p-0 m-0 text-sm text-color-secondary">
                <li className="mb-1">{selected.original_name}</li>
                <li className="mb-1">
                  {formatBytes(selected.file_size)}
                  {selected.width ? ` · ${selected.width}×${selected.height}px` : ""}
                </li>
                {selected.uploaderName && <li>Uploaded by {selected.uploaderName}</li>}
              </ul>

              <Divider />

              <div className="flex flex-column gap-2">
                {flash && (
                  <div className="media-flash">
                    <i className="pi pi-check-circle" /> {flash}
                  </div>
                )}
                <Button label="Save details" icon="pi pi-check" loading={saving} onClick={handleSave} />
                <Button label="Copy link" icon="pi pi-copy" outlined onClick={copyUrl} />
                <Button
                  label="Delete"
                  icon="pi pi-trash"
                  severity="danger"
                  text
                  onClick={handleDelete}
                />
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MediaLibrary;
